/**
 * useImages.js — gestione immagini: camera nativa, upload con fallback offline
 *
 * Pattern identico a useBluetooth.js:
 * - dynamic import di @capacitor/camera guardato da isNative()
 * - coda locale su IndexedDB (tabella image_upload_queue) se offline
 * - flushQueue() processa la coda quando la rete torna disponibile
 *
 * NOTA: image_upload_queue è una tabella locale-only.
 * Non usa dbStore.insert/delete (che imposterebbero _to_synch: true e
 * includerebbero i payload base64 nel ciclo di sync verso il server).
 * Usa invece Dexie direttamente tramite dbStore.db.
 */

import { ref } from 'vue'
import { isNative } from '../services/platform.js'
import { api } from '../services/api.js'
import { useAppStore } from '../stores/app.js'
import { useDbStore } from '../stores/dbStore.js'

// ── Camera plugin cache ──────────────────────────────────────────────────────

let _cameraCache = null

async function _getCamera() {
  if (!isNative()) return null
  if (_cameraCache) return _cameraCache
  try {
    const mod = await import(/* @vite-ignore */ '@capacitor/camera')
    _cameraCache = mod
    return mod
  } catch (err) {
    console.warn('[useImages] @capacitor/camera non disponibile:', err.message)
    return null
  }
}

// ── Singleton state ──────────────────────────────────────────────────────────

const queueCount = ref(0)
let _flushInProgress = false
let _globalListenersAttached = false

// ── Internal helpers ─────────────────────────────────────────────────────────

async function _refreshQueueCount() {
  const dbStore = useDbStore()
  if (!dbStore.db || !dbStore.isReady) return
  try {
    const items = await dbStore.db.table('image_upload_queue').toArray()
    queueCount.value = items.length
  } catch (_) {
    queueCount.value = 0
  }
}

async function _enqueue(guid, base64, { record, type, note }) {
  const dbStore = useDbStore()
  // Insert diretto su Dexie: image_upload_queue è locale, NON va sincronizzata
  // (dbStore.insert imporrebbe _to_synch: true e manderebbe base64 al server)
  await dbStore.db.table('image_upload_queue').add({
    id_queue: crypto.randomUUID(),
    guid_image: guid,
    base64,
    guid_record: record,
    table_ref: type,
    note: note || '',
    created_at: new Date().toISOString()
  })
  await _refreshQueueCount()
}

/**
 * Registra listener globali (singleton).
 * Le immagini vengono caricate SOLO dopo che le tabelle normali sono sincronizzate
 * (evento poderi:sync-complete dispatched da syncToServer).
 * Gestisce il caso in cui il componente ImageLoader sia smontato quando la rete torna.
 */
function _attachGlobalListeners() {
  if (_globalListenersAttached) return
  _globalListenersAttached = true

  // Trigger principale: syncToServer ha completato (OK) → ora è sicuro caricare le immagini
  window.addEventListener('poderi:sync-complete', () => {
    flushQueue(null).catch(err => console.warn('[useImages] flush after sync error:', err))
  })
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Apre la fotocamera o la galleria su nativo, oppure restituisce null su web.
 * @param {'camera'|'gallery'} source - sorgente esplicita
 * @returns {Promise<string|null>} data URL base64 o null su web
 */
async function pickImage(source = 'camera') {
  const cam = await _getCamera()
  if (!cam) return null

  const { Camera, CameraResultType, CameraSource } = cam
  const photo = await Camera.getPhoto({
    quality: 80,
    allowEditing: false,
    resultType: CameraResultType.DataUrl,
    source: source === 'gallery' ? CameraSource.Photos : CameraSource.Camera,
    saveToGallery: false
  })
  return photo.dataUrl // 'data:image/jpeg;base64,...'
}

/**
 * Carica un'immagine sul server. Se offline o errore rete → accoda in IndexedDB.
 * @param {string} guid - guid dell'immagine
 * @param {string} base64 - data URL base64
 * @param {{ record: string, type: string, note?: string }} meta
 * @returns {Promise<{ ok: boolean, queued?: boolean }>}
 */
async function uploadImage(guid, base64, { record, type, note = '' }) {
  const appStore = useAppStore()
  try {
    const result = await api.post(`/api/v2/poderi2/store_images/${guid}`, {
      access_token: appStore.accessToken,
      base64,
      guid_survey: record,
      table_ref: type,
      note
    })
    if (!result.ok) throw new Error(result.message || 'Errore server')
    return { ok: true }
  } catch (err) {
    if (!navigator.onLine || err.message === 'Failed to fetch') {
      await _enqueue(guid, base64, { record, type, note })
      return { ok: false, queued: true }
    }
    throw err
  }
}

/**
 * Processa la coda quando la rete è disponibile.
 * Elimina le righe dopo upload confermato (via Dexie diretto, senza _to_synch).
 * @param {import('vue').Ref<Array>|null} imagesRef - ref alle immagini correnti (per aggiornare synced), null = flush globale senza UI
 * @returns {Promise<void>}
 */
async function flushQueue(imagesRef) {
  if (!navigator.onLine) return
  if (_flushInProgress) return
  _flushInProgress = true

  try {
    const dbStore = useDbStore()
    const appStore = useAppStore()
    if (!dbStore.db || !dbStore.isReady) return

    const items = await dbStore.db.table('image_upload_queue').toArray()
    if (items.length === 0) return

    for (const item of items) {
      try {
        const res = await api.post(`/api/v2/poderi2/store_images/${item.guid_image}`, {
          access_token: appStore.accessToken,
          base64: item.base64,
          guid_survey: item.guid_record,
          table_ref: item.table_ref,
          note: item.note
        })
        if (res.ok) {
          // Delete diretto su Dexie: niente _to_synch, niente payload al server
          await dbStore.db.table('image_upload_queue').delete(item.id_queue)
          if (imagesRef?.value) {
            const img = imagesRef.value.find(i => i.guid === item.guid_image)
            if (img) img.synced = true
          }
        }
      } catch (_) {
        // upload ancora fallito, lascia in coda
      }
    }
  } finally {
    _flushInProgress = false
    await _refreshQueueCount()
  }
}

export function useImages() {
  // Aggiorna contatore all'inizializzazione
  _refreshQueueCount()
  // Registra listener globali (una sola volta per tutta l'app)
  _attachGlobalListeners()

  return {
    pickImage,
    uploadImage,
    flushQueue,
    queueCount
  }
}
